import type { IIconStyles, IStackStyles } from '@fluentui/react';
import { FontSizes, IH2OTheme } from '@h2oai/ui-kit';
import type { CSSProperties } from 'react';

import { DOCS_OPEN_NEW_TAB_BUTTON_BG, DOCS_OPEN_NEW_TAB_BUTTON_BORDER, RESIZE_HANDLE_WIDTH_PX } from '../constants';

export const getDocsRootStackStyles: Partial<IStackStyles> = {
  root: {
    flex: 1,
    width: '100%',
    height: '100vh',
    maxHeight: '100vh',
    minHeight: 0,
    alignItems: 'stretch',
    overflow: 'hidden',
  },
};

export const getDocsMainContentStackItemStyles: Partial<IStackStyles> = {
  root: {
    minWidth: 0,
    minHeight: 0,
    overflow: 'auto',
    height: '100%',
    maxHeight: '100%',
    position: 'relative',
    zIndex: 0,
  },
};

export type DocsDockOuterStyleParams = {
  dockWidthPx: number;
  isOverlayMode: boolean;
  isOpen: boolean;
  panelWidthTransition: string;
};

export const getDocsDockOuterStyle = ({
  dockWidthPx,
  isOverlayMode,
  isOpen,
  panelWidthTransition,
}: DocsDockOuterStyleParams): CSSProperties => ({
  width: dockWidthPx,
  flexShrink: 0,
  height: '100%',
  maxHeight: '100%',
  minHeight: 0,
  overflow: isOpen ? 'visible' : 'hidden',
  transition: panelWidthTransition,
  display: 'flex',
  alignItems: 'stretch',
  position: 'relative',
  zIndex: isOverlayMode && isOpen ? 20 : 1,
});

export type DocsDockInnerStyleParams = {
  theme: IH2OTheme;
  innerWidthPx: number;
  innerMarginLeftPx: number;
  isOverlayMode: boolean;
  isOpen: boolean;
  isResizing: boolean;
};

export const getDocsDockInnerStyle = ({
  theme,
  innerWidthPx,
  innerMarginLeftPx,
  isOverlayMode,
  isOpen,
  isResizing,
}: DocsDockInnerStyleParams): CSSProperties => ({
  display: 'flex',
  flexDirection: 'row',
  width: innerWidthPx,
  minWidth: innerWidthPx,
  flexShrink: 0,
  marginLeft: innerMarginLeftPx,
  height: '100%',
  maxHeight: '100%',
  minHeight: 0,
  boxShadow: isOverlayMode && isOpen ? theme.palette?.black_a_8 : undefined,
  position: 'relative',
  overflow: isOpen ? 'visible' : 'hidden',
  transition: isResizing ? 'none' : undefined,
});

export const getDocsResizeHandleStyle = (isOpen: boolean): CSSProperties => ({
  width: RESIZE_HANDLE_WIDTH_PX,
  flexShrink: 0,
  alignSelf: 'stretch',
  cursor: 'col-resize',
  backgroundColor: 'rgba(0,0,0,0.015)',
  backgroundImage:
    'linear-gradient(to right, transparent 0, transparent 2px, rgba(0,0,0,0.22) 2px, rgba(0,0,0,0.22) 4px, transparent 4px)',
  boxShadow: 'inset 1px 0 0 rgba(0,0,0,0.04), inset -1px 0 0 rgba(0,0,0,0.04)',
  visibility: isOpen ? 'visible' : 'hidden',
  pointerEvents: isOpen ? 'auto' : 'none',
});

export type DocsSidebarPanelStyleParams = {
  theme: IH2OTheme;
  sidebarWidthPx: number;
};

export const getDocsSidebarPanelStyle = ({ theme, sidebarWidthPx }: DocsSidebarPanelStyleParams): CSSProperties => ({
  width: sidebarWidthPx,
  flexShrink: 0,
  borderLeft: `1px solid ${theme.palette?.gray300}`,
  backgroundColor: theme.semanticColors?.contentBackground,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  maxHeight: '100%',
  minHeight: 0,
});

export const getDocsSidebarStackStyles = (): Partial<IStackStyles> => ({
  root: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
});

export const getDocsBodyStackItemStyles: Partial<IStackStyles> = {
  root: {
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
};

export const getDocsScrollAreaStyle: CSSProperties = {
  flex: 1,
  minHeight: 0,
  padding: 0,
  overflow: 'hidden',
};

export const DOCS_DOCK_CONTROL_SIZE_PX = 36;
export const DOCS_DOCK_CONTROL_GAP_PX = 8;

/** Stack for close + open-in-new-tab controls on the docs panel gutter. */
export const getDocsDockControlsStyle = (): CSSProperties => ({
  position: 'absolute',
  top: 16,
  left: 0,
  transform: 'translateX(-50%)',
  zIndex: 30,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: DOCS_DOCK_CONTROL_GAP_PX,
});

const getDocsDockControlButtonBaseStyle = (): CSSProperties => ({
  width: DOCS_DOCK_CONTROL_SIZE_PX,
  height: DOCS_DOCK_CONTROL_SIZE_PX,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  padding: 0,
  margin: 0,
  borderRadius: '50%',
  cursor: 'pointer',
  outline: 'none',
});

/** Collapse control — light surface on the gutter. */
export const getDocsCollapseButtonStyle = (theme: IH2OTheme): CSSProperties => ({
  ...getDocsDockControlButtonBaseStyle(),
  border: `1.5px solid ${theme.palette?.gray400}`,
  backgroundColor: theme.palette?.white,
  boxShadow: `0 0 0 1px ${theme.palette?.black_a_4 ?? 'rgba(0,0,0,0.04)'}, 0 4px 12px ${
    theme.palette?.gray900_a_60 ?? 'rgba(8,8,8,0.22)'
  }`,
});

/** Open in new tab — info blue accent. */
export const getDocsOpenInNewTabButtonStyle = (theme: IH2OTheme): CSSProperties => {
  const backgroundColor =
    theme.palette?.blue500 ?? theme.semanticColors?.messageBarIconInfo ?? DOCS_OPEN_NEW_TAB_BUTTON_BG;
  const borderColor =
    theme.palette?.blue600 ??
    theme.palette?.blue700 ??
    theme.semanticColors?.linkText ??
    DOCS_OPEN_NEW_TAB_BUTTON_BORDER;

  return {
    ...getDocsDockControlButtonBaseStyle(),
    border: `1.5px solid ${borderColor}`,
    backgroundColor,
    boxShadow: `0 0 0 1px ${theme.palette?.black_a_8 ?? 'rgba(0,0,0,0.08)'}, 0 4px 12px ${
      theme.palette?.gray900_a_60 ?? 'rgba(8,8,8,0.35)'
    }`,
  };
};

export const getDocsCollapseButtonIconStyle = (theme: IH2OTheme): Partial<IIconStyles> => ({
  root: {
    color: theme.palette?.gray900 ?? theme.semanticColors?.textPrimary ?? '#212121',
    fontSize: FontSizes.medium,
    lineHeight: FontSizes.medium,
    fontWeight: 600,
  },
});

export const getDocsOpenInNewTabButtonIconStyle = (theme: IH2OTheme): Partial<IIconStyles> => ({
  root: {
    color: theme.palette?.white ?? theme.palette?.textOverPrimary ?? '#ffffff',
    fontSize: FontSizes.medium,
    lineHeight: FontSizes.medium,
    fontWeight: 600,
  },
});
